import * as React from 'react'
import { gsap } from 'gsap'

import { useHasRendered } from './use-has-rendered'
import { useMouseTracker } from './use-mouse-tracker'

const getWebkitMaskImage = (x: number, y: number) =>
  `radial-gradient(var(--radius-x) var(--radius-y) at ${x}px ${y}px, black 45%,transparent)`

export const useLantern = <
  T extends HTMLElement,
  WrapperT extends HTMLElement = HTMLDivElement,
>({
  size,
  isFocused,
  initial: _initial,
}: {
  size: 'sm' | 'md' | 'lg'
  isFocused?: boolean
  initial?: {
    x?: number
    y?: number
    opacity?: number
    backAtRest?: boolean
  }
}) => {
  const maskRef = React.useRef<T>(null)
  const [isFocusing, setIsFocusing] = React.useState(isFocused ?? false)
  const [initial] = React.useState(_initial)
  const hasHoveredOnceRef = React.useRef(false)

  // don't ask me why, but this extra rerender is making the animation work
  // when we migrated to the appDir (https://github.com/basementstudio/basehub/pull/43)
  // the animation stopped working, and this is the only thing that fixed it
  // i belive its because the animation needed to rerender (cause its badly programmed)
  // and the appDir is actually saving us some rerenders, but also making this animation not work?
  // TODO: fix this
  useHasRendered()

  const sizes = React.useMemo(() => {
    if (size === 'sm') {
      return {
        '--radius-x': isFocusing ? '190%' : '30%',
        '--radius-y': isFocusing ? '800px' : '30px',
        '--opacity': isFocusing ? 1 : 0,
      }
    }

    if (size === 'md') {
      return {
        '--radius-x': isFocusing ? '290%' : '80%',
        '--radius-y': isFocusing ? '1800px' : '80px',
        '--opacity': isFocusing ? 1 : 0,
      }
    }

    if (size === 'lg') {
      return {
        '--radius-x': isFocusing ? '390%' : '130%',
        '--radius-y': isFocusing ? '2800px' : '130px',
        '--opacity': isFocusing ? 1 : 0,
      }
    }
  }, [size, isFocusing])

  const { elementRef } = useMouseTracker<WrapperT>(
    React.useMemo(() => {
      return {
        onChange: ({ x, y, isHovering, first }) => {
          if (isHovering) {
            hasHoveredOnceRef.current = true
          }

          if (!maskRef.current) return
          if (isFocusing) return
          let opacity = isHovering ? 1 : 0
          if ((first || (!isHovering && initial?.backAtRest)) && initial) {
            x = initial.x ?? x
            y = initial.y ?? y
            opacity = initial.opacity ?? opacity
          }

          if (first) {
            gsap.set(maskRef.current, {
              webkitMaskImage: getWebkitMaskImage(x, y),
            })
            gsap.to(maskRef.current, {
              ...sizes,
              '--opacity': opacity,
              duration: 0.5,
            })
          } else {
            if (initial && !isHovering && !hasHoveredOnceRef.current) {
              return
            }

            gsap.to(maskRef.current, {
              ...sizes,
              '--opacity': opacity,
              webkitMaskImage: getWebkitMaskImage(x, y),
              duration: 0.5,
            })
          }
        },
        windowAsProxy: true,
        // enableOnlyWhenHovering: true,
      }
    }, [isFocusing, sizes, initial])
  )

  React.useEffect(() => {
    if (!maskRef.current) return

    const tween = gsap.to(maskRef.current, {
      ...sizes,
      ...(typeof initial?.opacity === 'number'
        ? { '--opacity': initial.opacity }
        : {}),
      ...(typeof initial?.x === 'number' && typeof initial?.y === 'number'
        ? { webkitMaskImage: getWebkitMaskImage(initial.x, initial.y) }
        : {}),
    })

    return () => {
      tween.kill()
    }
  }, [sizes, initial])

  return {
    maskRef,
    elementRef,
    focusHandlers: {
      onFocus: () => setIsFocusing(true),
      onBlur: () => setIsFocusing(false),
    },
  }
}
